import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import AppHeader from '../components/AppHeader';
import ToolboxParticipantRow from '../components/ToolboxParticipantRow';
import useToolboxData from '../hooks/useToolboxData';
import AppPageLayout from '../layout/AppPageLayout';
import { useToolboxStore } from '../store/toolboxStore';

function ToolboxPage() {
  const navigate = useNavigate();
  const { tipoOperativos, lugares, loading, error, cargarTipoOperativos, cargarLugares, buscarParticipantes } = useToolboxData();
  const filters = useToolboxStore((state) => state.filters);
  const hasSearchedParticipants = useToolboxStore(
    (state) => state.hasSearchedParticipants
  );
  const participantsDisponibles = useToolboxStore(
    (state) => state.participantsDisponibles
  );
  const participantSearch = useToolboxStore((state) => state.participantSearch);
  const participantsSeleccionados = useToolboxStore(
    (state) => state.participantsSeleccionados
  );
  const setOperationId = useToolboxStore((state) => state.setOperationId);
  const setPlaceId = useToolboxStore((state) => state.setPlaceId);
  const setDate = useToolboxStore((state) => state.setDate);
  const setStartTime = useToolboxStore((state) => state.setStartTime);
  const setEndTime = useToolboxStore((state) => state.setEndTime);
  const volverAFiltros = useToolboxStore((state) => state.volverAFiltros);
  const setParticipantSearch = useToolboxStore(
    (state) => state.setParticipantSearch
  );
  const toggleParticipant = useToolboxStore((state) => state.toggleParticipant);
  const storeSetParticipantes = useToolboxStore((state) => state.buscarParticipantes);

  const operationOptions = [
    { value: '', label: 'Seleccione Tipo' },
    ...tipoOperativos.map((t) => ({ value: t.id, label: t.Descripcion })),
  ];

  const placeOptions = [
    { value: '', label: 'Seleccione Lugar' },
    ...lugares.map((l) => ({ value: l.id, label: l.descript })),
  ];

  useEffect(() => {
    cargarTipoOperativos();
  }, [cargarTipoOperativos]);

  useEffect(() => {
    cargarLugares(filters.operationId);
  }, [filters.operationId, cargarLugares]);

  const filteredParticipants = useMemo(() => {
    const query = participantSearch.trim().toLowerCase();

    if (!query) {
      return participantsDisponibles;
    }

    return participantsDisponibles.filter((item) =>
      `${item.nombre} ${item.legajo}`.toLowerCase().includes(query)
    );
  }, [participantSearch, participantsDisponibles]);

  const selectedParticipantIds = useMemo(
    () => new Set(participantsSeleccionados.map((item) => item.id)),
    [participantsSeleccionados]
  );

  const handleBack = () => {
    if (hasSearchedParticipants) {
      volverAFiltros();
      return;
    }

    navigate('/dashboard');
  };

  const handleSearchParticipants = async () => {
    try {
      const result = await buscarParticipantes({
        tipoOperativo: filters.operationId,
        lugarOperativo: filters.placeId,
        fecha: filters.date,
      });
      storeSetParticipantes(result);
    } catch (err) {
      return err;
    }
  };

  return (
    <AppPageLayout>
      <AppHeader />

      <div className="mx-auto mt-10 w-full max-w-[820px] lg:mt-12">
        <h1 className="text-center font-login text-[30px] font-bold leading-tight text-[#30343b] sm:text-[36px]">
          ToolBox
        </h1>

        <div className="mt-8 border-t border-[#eceff3] pt-6">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
          </button>

          {!hasSearchedParticipants ? (
            <div className="mt-6 rounded-[22px] border border-[#dfe6ed] bg-white p-5 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-6">
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <label
                    htmlFor="toolbox-operation"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Tipo Operaciones
                  </label>
                  <select
                    id="toolbox-operation"
                    value={filters.operationId}
                    onChange={(event) => setOperationId(event.target.value)}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  >
                    {operationOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="toolbox-place"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Lugares Operativos
                  </label>
                  <select
                    id="toolbox-place"
                    value={filters.placeId}
                    onChange={(event) => setPlaceId(event.target.value)}
                    disabled={!filters.operationId}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5] disabled:cursor-not-allowed disabled:bg-[#f3f5f7] disabled:text-[#9aa3ad]"
                  >
                    {placeOptions.map((option) => (
                      <option key={option.value || 'placeholder'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="toolbox-date"
                    className="block font-login text-[14px] font-bold text-[#30343b]"
                  >
                    Fecha
                  </label>
                  <input
                    id="toolbox-date"
                    type="date"
                    value={filters.date}
                    onChange={(event) => setDate(event.target.value)}
                    className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label
                      htmlFor="toolbox-start-time"
                      className="block font-login text-[14px] font-bold text-[#30343b]"
                    >
                      Hora Inicio
                    </label>
                    <input
                      id="toolbox-start-time"
                      type="time"
                      value={filters.startTime}
                      onChange={(event) => setStartTime(event.target.value)}
                      className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="toolbox-end-time"
                      className="block font-login text-[14px] font-bold text-[#30343b]"
                    >
                      Hora Fin
                    </label>
                    <input
                      id="toolbox-end-time"
                      type="time"
                      value={filters.endTime}
                      onChange={(event) => setEndTime(event.target.value)}
                      className="h-11 w-full rounded-[10px] border border-[#cfd7df] bg-white px-4 font-login text-[14px] text-[#374151] outline-none transition focus:border-[#2e86b5]"
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSearchParticipants}
                disabled={!filters.operationId || !filters.placeId || loading}
                className="mt-6 w-full rounded-[12px] bg-[#7f9db1] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Cargando operarios...' : 'Buscar Participantes'}
              </button>
            </div>
          ) : (
            <div className="mt-6 rounded-[22px] border border-[#dfe6ed] bg-white p-4 shadow-[0_16px_40px_rgba(15,23,42,0.08)] sm:p-6">
              <div className="flex overflow-hidden rounded-[10px] border border-[#d7dce2] bg-white shadow-sm">
                <input
                  type="text"
                  value={participantSearch}
                  onChange={(event) => setParticipantSearch(event.target.value)}
                  placeholder="Legajo / Nombre"
                  className="h-11 flex-1 border-0 px-4 font-login text-[14px] text-[#374151] outline-none placeholder:text-[#9aa3ad]"
                />
                <button
                  type="button"
                  aria-label="Buscar participante"
                  className="flex h-11 w-12 items-center justify-center bg-[#14686a] text-white"
                >
                  <Search className="h-4 w-4" strokeWidth={2.8} />
                </button>
              </div>

              <div className="mt-4 overflow-hidden rounded-[12px] border border-[#dce2e8] bg-white">
                <div className="grid grid-cols-[1.5fr_0.9fr] gap-3 border-b border-[#dce2e8] bg-[#f7f8fa] px-3 py-3">
                  <span className="font-login text-[11px] font-bold text-[#30343b] sm:text-[12px]">
                    Nombre
                  </span>
                  <span className="font-login text-[11px] font-bold text-[#30343b] sm:text-[12px]">
                    Legajo
                  </span>
                </div>

                <div className="max-h-[340px] overflow-auto">
                  {error ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#c43c2f]">
                      No se pudo cargar la lista de operarios.
                    </p>
                  ) : null}

                  {!error && !filteredParticipants.length ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#5f6673]">
                      No hay operarios para mostrar.
                    </p>
                  ) : null}

                  {filteredParticipants.map((item) => (
                    <ToolboxParticipantRow
                      key={item.id}
                      nombre={item.nombre}
                      legajo={item.legajo}
                      isSelected={selectedParticipantIds.has(item.id)}
                      onClick={() => toggleParticipant(item)}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-login text-[14px] font-semibold text-[#526070]">
                  Seleccionados: {participantsSeleccionados.length}
                </p>

                <button
                  type="button"
                  onClick={() => navigate('/toolbox/carga')}
                  disabled={!participantsSeleccionados.length}
                  className="w-full rounded-[12px] bg-[#205f8d] px-4 py-4 font-login text-[15px] font-bold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[220px]"
                >
                  Confirmar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="pt-10 text-center font-login text-[10px] text-[#5f6673]">
          <p>Copyright 2022 JPH Lions Todos los derechos reservados</p>
        </div>
      </div>
    </AppPageLayout>
  );
}

export default ToolboxPage;
